import { useState, useRef, useEffect } from 'react'
import { cn } from '../../lib/utils'
import type { Agent } from '../../pages/AgentsPage'

type Subject = { id: number; title: string; source: string }

interface AgentCardProps {
  agent: Agent
  t: any
  language: string
  numberFormatter: Intl.NumberFormat
  dateFormatter: Intl.DateTimeFormat
  subjectMap: Map<number, Subject>
  iconUploadingId: number | null
  deletePending: boolean
  deleteTarget: Agent | null
  onIconChange: (agentId: number) => (e: React.ChangeEvent<HTMLInputElement>) => void
  onSelectForChat: (agentId: number) => void
  onNavigate: (path: string) => void
  onDuplicate: (agentId: number, agentName: string) => void
  onToggleActivation: (agentId: number, currentlyActive: boolean) => void
  onOpenMcp: (agentId: number, agentName: string) => void
  onDelete: (agent: Agent) => void
}

export default function AgentCard({
  agent,
  t,
  language,
  numberFormatter,
  dateFormatter,
  subjectMap,
  iconUploadingId,
  deletePending,
  deleteTarget,
  onIconChange,
  onSelectForChat,
  onNavigate,
  onDuplicate,
  onToggleActivation,
  onOpenMcp,
  onDelete,
}: AgentCardProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    if (menuOpen) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  const initial = agent.name.charAt(0).toUpperCase()
  const msgCount = numberFormatter.format(agent.usage.message_count)
  const msgLimit = agent.usage.limit != null ? ` / ${numberFormatter.format(agent.usage.limit)}` : ''
  const updatedLabel = dateFormatter.format(new Date(agent.updated_at))
  const docsCount = agent.subject_ids.length

  return (
    <article className={cn(
      'rounded-2xl border bg-zinc-900/80 shadow-sm transition-all hover:border-zinc-700/60',
      agent.is_active
        ? 'border-t-2 border-t-emerald-500 border-x-zinc-800/60 border-b-zinc-800/60'
        : 'border-zinc-800/60'
    )}>
      <div className="p-4">
        {/* ── Row 1 : avatar + nom + statut + menu ── */}
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <label className="relative flex-shrink-0 cursor-pointer group" title={t.agents.agentCard.changeIcon}>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              className="hidden"
              onChange={onIconChange(agent.id)}
            />
            <div className="h-10 w-10 rounded-xl overflow-hidden bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center transition group-hover:border-emerald-400/60">
              {iconUploadingId === agent.id ? (
                <svg className="h-4 w-4 animate-spin text-emerald-400" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                </svg>
              ) : agent.icon_preview_url ? (
                <img src={agent.icon_preview_url} alt={agent.name} className="h-full w-full object-cover" />
              ) : (
                <span className="text-sm font-bold text-emerald-400">{initial}</span>
              )}
            </div>
          </label>

          {/* Infos */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-white truncate">{agent.name}</h3>
              <span className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                agent.is_active
                  ? 'bg-emerald-500/15 text-emerald-400'
                  : 'bg-zinc-700/60 text-zinc-400'
              )}>
                <span className={cn('h-1.5 w-1.5 rounded-full', agent.is_active ? 'bg-emerald-400' : 'bg-zinc-500')} />
                {agent.is_active ? t.agents.agentCard.active : t.agents.agentCard.inactive}
              </span>
            </div>
            <p className="text-[11px] text-zinc-500 truncate mt-0.5">{agent.slug}</p>
          </div>

          {/* Menu contextuel */}
          <div className="relative flex-shrink-0" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen(v => !v)}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300 transition-colors"
              aria-label="Plus d'options"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>

            {menuOpen && (
              <div className="absolute right-0 bottom-9 z-30 w-48 rounded-xl border border-zinc-700/60 bg-zinc-900 shadow-2xl py-1 overflow-hidden">
                <MenuItem
                  icon={<IconCopy />}
                  label={language === 'fr' ? 'Dupliquer' : language === 'es' ? 'Duplicar' : 'Duplicate'}
                  onClick={() => { onDuplicate(agent.id, agent.name); setMenuOpen(false) }}
                />
                <MenuItem
                  icon={<IconPlug />}
                  label={t.agents.agentCard.mcp}
                  onClick={() => { onOpenMcp(agent.id, agent.name); setMenuOpen(false) }}
                />
                <MenuItem
                  icon={<IconMail />}
                  label={t.agents.agentCard.assignEmail}
                  onClick={() => { onNavigate(`/email?assign_agent=${agent.id}`); setMenuOpen(false) }}
                />
                <div className="h-px bg-zinc-800 my-1" />
                <MenuItem
                  icon={agent.is_active ? <IconPause /> : <IconPlay />}
                  label={agent.is_active ? t.agents.agentCard.deactivate : t.agents.agentCard.activate}
                  onClick={() => { onToggleActivation(agent.id, agent.is_active); setMenuOpen(false) }}
                />
                <div className="h-px bg-zinc-800 my-1" />
                <MenuItem
                  icon={<IconTrash />}
                  label={deletePending && deleteTarget?.id === agent.id ? t.agents.agentCard.deleting : t.agents.agentCard.delete}
                  danger
                  disabled={deletePending && deleteTarget?.id === agent.id}
                  onClick={() => { onDelete(agent); setMenuOpen(false) }}
                />
              </div>
            )}
          </div>
        </div>

        {/* ── Row 2 : stats compactes ── */}
        <div className="mt-3 flex items-center gap-3 text-[11px] text-zinc-500">
          <StatPill
            icon={<IconMsg />}
            value={`${msgCount}${msgLimit}`}
            label={t.agents.agentCard.messages}
            tooltip={`${language === 'fr' ? 'Conversations' : language === 'es' ? 'Conversaciones' : 'Conversations'} : ${msgCount}${msgLimit}`}
          />
          <span className="text-zinc-700">·</span>
          <StatPill
            icon={<IconBook />}
            value={`${docsCount}`}
            label={t.agents.agentCard.docs}
            tooltip={`${language === 'fr' ? 'Bases de connaissances' : language === 'es' ? 'Bases de conocimiento' : 'Knowledge bases'} : ${docsCount}`}
          />
          <span className="text-zinc-700">·</span>
          <StatPill
            icon={<IconBrain />}
            value={agent.model_id ? agent.model_id.split('/').pop()! : 'Default'}
            tooltip={`${language === 'fr' ? 'Modèle IA' : language === 'es' ? 'Modelo IA' : 'AI model'} : ${agent.model_id || 'Default'}`}
          />
        </div>

        {/* ── Row 3 : instructions ── */}
        {agent.instructions && (
          <p className="mt-3 text-xs text-zinc-400 line-clamp-2 leading-relaxed">
            {agent.instructions}
          </p>
        )}

        {/* ── Row 4 : bases de connaissance ── */}
        {agent.subject_ids.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {agent.subject_ids.slice(0, 3).map(id => {
              const subject = subjectMap.get(id)
              if (!subject) return null
              return (
                <span key={id} className="inline-flex items-center gap-1.5 rounded-full bg-zinc-800/80 border border-zinc-700/50 px-2.5 py-0.5 text-[11px] text-zinc-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                  <span className="truncate max-w-[100px]">{subject.title}</span>
                </span>
              )
            })}
            {agent.subject_ids.length > 3 && (
              <span className="inline-flex items-center rounded-full bg-zinc-800/80 border border-zinc-700/50 px-2.5 py-0.5 text-[11px] text-zinc-500">
                +{agent.subject_ids.length - 3}
              </span>
            )}
          </div>
        )}

        {/* ── Row 5 : actions primaires ── */}
        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={() => onSelectForChat(agent.id)}
            className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-2 text-xs font-semibold text-white shadow-sm shadow-emerald-500/20 hover:bg-emerald-600 transition-colors"
          >
            <IconChat />
            {t.agents.agentCard.useInChat}
          </button>

          <button
            type="button"
            onClick={() => onNavigate(`/conversations?agent_id=${agent.id}`)}
            className="flex items-center justify-center gap-1.5 rounded-lg border border-zinc-700/60 px-3 py-2 text-xs font-medium text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors"
          >
            <IconConv />
            <span className="hidden sm:inline">{t.agents.agentCard.history}</span>
          </button>

          <button
            type="button"
            onClick={() => onNavigate(`/agents/${agent.id}/edit`)}
            data-onboarding="agent-edit-btn"
            className="flex items-center justify-center gap-1.5 rounded-lg border border-zinc-700/60 px-3 py-2 text-xs font-medium text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors"
          >
            <IconEdit />
            <span className="hidden sm:inline">{t.agents.agentCard.edit}</span>
          </button>

          <div className="text-[10px] text-zinc-600 ml-auto hidden sm:block whitespace-nowrap">
            {updatedLabel}
          </div>
        </div>
      </div>
    </article>
  )
}

/* ── Sub-components ────────────────────────────────────────────── */

function MenuItem({ icon, label, onClick, danger = false, disabled = false }: {
  icon: React.ReactNode
  label: string
  onClick: () => void
  danger?: boolean
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium transition-colors disabled:opacity-40',
        danger
          ? 'text-red-400 hover:bg-red-500/10'
          : 'text-zinc-300 hover:bg-zinc-800'
      )}
    >
      <span className="flex-shrink-0 opacity-70">{icon}</span>
      {label}
    </button>
  )
}

function StatPill({ icon, value, label, tooltip }: { icon: React.ReactNode; value: string; label?: string; tooltip?: string }) {
  const [show, setShow] = useState(false)
  return (
    <span
      className="relative inline-flex items-center gap-1 text-zinc-500 cursor-default"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onTouchStart={() => setShow(v => !v)}
    >
      <span className="opacity-60">{icon}</span>
      <span className="font-medium text-zinc-400">{value}</span>
      {label && <span className="hidden sm:inline text-zinc-600">{label}</span>}
      {tooltip && show && (
        <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 whitespace-nowrap rounded-lg bg-zinc-800 border border-zinc-700/60 px-2.5 py-1.5 text-[11px] text-zinc-200 shadow-xl">
          {tooltip}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-800" />
        </span>
      )}
    </span>
  )
}

/* ── Icons (inline SVG, taille h-3.5 w-3.5) ────────────────────── */
const IconChat = () => (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
)
const IconConv = () => (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
  </svg>
)
const IconEdit = () => (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
)
const IconCopy = () => (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
)
const IconPlug = () => (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
)
const IconMail = () => (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
)
const IconPause = () => (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 9v6m4-6v6" />
  </svg>
)
const IconPlay = () => (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)
const IconTrash = () => (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
)
const IconMsg = () => (
  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
  </svg>
)
const IconBook = () => (
  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
)
const IconBrain = () => (
  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  </svg>
)
